/* eslint-disable no-use-before-define */
import React, { useState, useEffect } from "react";

import { HurdleProps } from "underwriter_dashboard/HurdleEditor";
import HurdleView from "underwriter_dashboard/shared/HurdleView";
import { LoadingState } from "underwriter_dashboard/shared/types";
import {
  createPrepaid,
  deletePrepaid,
  fetchWorksheetPrepaids,
  importPrepaids,
  updatePrepaid,
} from "underwriter_dashboard/hurdle_editors/Prepaids/api";

import { formatCurrency } from "@eave/shared/utils/currency";

import BaseTable, { EditingState } from "web_modules/shared/BaseTable";
import {
  Button,
  InlineNotification,
  Form,
  FormGroup,
  Loading,
  Modal,
} from "carbon-components-react";

import { makeHeaderData } from "web_modules/shared/BaseTable/helpers";
import { CarbonInputGenerationParams } from "web_modules/shared/carbon-types";

import Field from "./Field";
import { ENUM } from "../../shared/WorksheetEditor/wsDefs";
import { notDash, notEmpty } from "../../shared/WorksheetEditor/wsValidationHelpers";

const styles = require("./styles.module.scss");

const Prepaids: React.FC<HurdleProps> = (props: HurdleProps) => {
  useEffect(() => {
    fetchPrepaids();
  }, []);

  const { app, hurdle, onHide } = props;
  const [prepaids, setPrepaids] = useState(null);
  const [formState, setFormState] = useState(null as any);
  const [formUpdates, setFormUpdates] = useState(null as any);

  // UI
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
  const [editingMode, setEditingMode] = useState(EditingState.CREATE);
  const [invalidFields, setInvalidFields] = useState({});
  const [loadingState, setLoadingState] = useState(LoadingState.INACTIVE);
  const [errorMessage, setErrorMessage] = useState("");

  const validateForm = (name, form, validation) => {
    if (!validation) {
      return;
    }

    const copy = invalidFields;

    // clear this field's prior run validation
    delete copy[name];

    // and reset with new msg, if invalid
    const value = form[name];
    const invalidMsg = validation(value);
    if (invalidMsg !== "") {
      copy[name] = invalidMsg;
    }

    setInvalidFields(copy);
  };

  const handleChange = (name, value) => {
    // clear error msg
    setErrorMessage("");

    // just the updates, for persistence.
    setFormUpdates({ ...formUpdates, [name]: value });

    const updatedForm = { ...formState, [name]: value };

    // the entire form state, with updates, for display in the modal.
    setFormState(updatedForm);

    // validate after update, since input fields will display even invalid results.
    const fieldDef = modalFields.find(field => field.name === name);
    validateForm(name, updatedForm, fieldDef.validation);
  };

  const fetchPrepaids = async () => {
    setLoadingState(LoadingState.ACTIVE);

    try {
      const { id, version, items } = await fetchWorksheetPrepaids(app);
      setPrepaids({
        id,
        version,
        items,
      });
    } catch (err) {
      setErrorMessage(err.meta.error_detail);
    } finally {
      setLoadingState(LoadingState.FINISHED);
    }
  };

  const tableHeaders = makeHeaderData({
    amount: "Amount",
    number_of_months: "Number of months",
    recipient: "Recipient",
    timing_type: "Timing type",
    paid_to: "Paid to",
    paid_by: "Paid by",
    item_type: "Item type",
  });

  const modalFields: CarbonInputGenerationParams[] = [
    {
      name: "amount",
      type: "text",
      label: "Amount *",
      placeholder: "400",
      prepareForAPI: v => {
        // avoiding super weird ws API commas handling, where sending "17,123" ->
        // persistence of "17" only
        const commas = /,/g;
        return v.replace(commas, "");
      },
      validation: v => {
        const notEmptyMsg = notEmpty(v);
        if (notEmptyMsg !== "") {
          return notEmptyMsg;
        }
        const commas = /,/g;
        const minusCommas = v.replace(commas, "");

        // not a perfect $ input regex, so not extracting.
        // but good enough for this use case.
        const validPattern = /^-?\d*\.?\d{0,2}$/;
        if (!validPattern.test(minusCommas)) {
          return "Only include numbers, with up to 2 decimal places (eg -4.05, 1,000)";
        }

        return "";
      },
    },
    {
      name: "number_of_months",
      type: "text",
      label: "Number of months *",
      placeholder: "2",
      validation: v => {
        const notEmptyMsg = notEmpty(v);
        if (notEmptyMsg !== "") {
          return notEmptyMsg;
        }

        const validPattern = /^\d*$/;
        if (!validPattern.test(v)) {
          return "Only include numbers";
        }

        return "";
      },
    },
    {
      name: "recipient",
      type: "text",
      label: "Recipient",
      placeholder: "Name",
    },
    {
      name: "timing_type",
      type: "select",
      label: "Timing type *",
      options: ["-", ...ENUM.PREPAID_TIMING],
      validation: notDash,
    },
    {
      name: "paid_to",
      type: "select",
      label: "Paid to *",
      options: ["-", ...ENUM.PREPAID_PAID_TO],
      validation: notDash,
    },
    {
      name: "paid_by",
      type: "select",
      label: "Paid by *",
      options: ["-", ...ENUM.PREPAID_PAID_BY],
      validation: notDash,
    },
    {
      name: "item_type",
      type: "combobox",
      label: "Item type *",
      options: ["-", ...ENUM.PREPAID_TYPE],
      validation: notDash,
    },
  ];

  return (
    <HurdleView className={styles.prepaidsHurdle} app={app} hurdle={hurdle} onHide={onHide}>
      <Modal
        danger
        open={deleteModalIsOpen}
        modalHeading="Are you sure you'd like to delete this record?"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        onBlur={() => {
          setErrorMessage("");
          setDeleteModalIsOpen(false);
        }}
        onRequestClose={() => {
          setErrorMessage("");
          setDeleteModalIsOpen(false);
        }}
        onRequestSubmit={() => {
          setLoadingState(LoadingState.ACTIVE);

          deletePrepaid(prepaids, formState)
            .then(() => {
              fetchPrepaids();
              setFormState(null);
              setDeleteModalIsOpen(false);
              setLoadingState(LoadingState.FINISHED);
            })
            .catch(err => {
              setErrorMessage(err.meta.error_detail);
              setLoadingState(LoadingState.FINISHED);
            });
        }}
      >
        {/* Loading spinner */}
        <Loading active={loadingState === LoadingState.ACTIVE} />

        {errorMessage.length > 0 && (
          <InlineNotification
            kind="error"
            notificationType="inline"
            role="alert"
            statusIconDescription="Error"
            subtitle={errorMessage}
            title="Error"
            onCloseButtonClick={() => {
              setErrorMessage("");
            }}
          />
        )}
      </Modal>

      <Modal
        open={modalIsOpen}
        modalHeading={editingMode === EditingState.CREATE ? "Add Prepaid" : "Update Prepaid"}
        primaryButtonText={editingMode === EditingState.CREATE ? "Save" : "Update"}
        secondaryButtonText="Cancel"
        primaryButtonDisabled={Object.keys(invalidFields).length > 0}
        onRequestClose={() => {
          setFormState(null);
          setFormUpdates(null);
          setErrorMessage("");
          setInvalidFields({});
          setModalIsOpen(false);
        }}
        onBlur={() => {
          setFormState(null);
          setFormUpdates(null);
          setErrorMessage("");
          setInvalidFields({});
          setModalIsOpen(false);
        }}
        onRequestSubmit={() => {
          // check all form fields, to confirm all are valid.
          const currentInvalidFields = modalFields.reduce((acc, fieldDef) => {
            if (!fieldDef.validation) {
              return acc;
            }
            const value = formState[fieldDef.name];
            const invalidMsg = fieldDef.validation(value);
            if (invalidMsg === "") {
              return acc;
            }
            return {
              ...acc,
              [fieldDef.name]: invalidMsg,
            };
          }, {});

          setInvalidFields(currentInvalidFields);

          if (Object.keys(currentInvalidFields).length > 0) {
            setErrorMessage("There are invalid fields. Please check again.");
            return;
          }

          if (editingMode === EditingState.CREATE) {
            setLoadingState(LoadingState.ACTIVE);

            createPrepaid(app, prepaids, formUpdates, modalFields)
              .then(() => {
                fetchPrepaids();
                setFormState(null);
                setFormUpdates(null);
                setModalIsOpen(false);
                setLoadingState(LoadingState.FINISHED);
              })
              .catch(err => {
                setErrorMessage(err.meta.error_detail);
                setLoadingState(LoadingState.FINISHED);
              });
          } else if (editingMode === EditingState.UPDATE) {
            setLoadingState(LoadingState.ACTIVE);

            const { id, version } = formState;

            updatePrepaid(id, version, formUpdates, modalFields)
              .then(() => {
                fetchPrepaids();
                setFormState(null);
                setFormUpdates(null);
                setModalIsOpen(false);
                setLoadingState(LoadingState.FINISHED);
              })
              .catch(err => {
                setErrorMessage(err.meta.error_detail);
                setLoadingState(LoadingState.FINISHED);
              });
          }
        }}
      >
        {/* Loading spinner */}
        <Loading active={loadingState === LoadingState.ACTIVE} />

        {errorMessage.length > 0 && (
          <InlineNotification
            kind="error"
            notificationType="inline"
            role="alert"
            statusIconDescription="Error"
            subtitle={errorMessage}
            title="Error"
            onCloseButtonClick={() => {
              setErrorMessage("");
            }}
          />
        )}

        {formState && modalIsOpen && (
          <>
            <p>Required fields are marked with an asterisk *</p>
            <Form onSubmit={() => {}}>
              <FormGroup legendText="">
                {modalFields.map(fieldDef => (
                  <div key={`${fieldDef.name}-wrapper`}>
                    <Field
                      fieldDef={fieldDef}
                      value={formState[fieldDef.name]}
                      invalidMsg={invalidFields[fieldDef.name]}
                      handleChange={handleChange}
                    />
                    <div key={`${fieldDef.name}-margin-bottom`} style={{ height: "10px" }} />
                  </div>
                ))}
                <div style={{ height: "20px" }} />
              </FormGroup>
            </Form>
          </>
        )}
      </Modal>

      {prepaids && (
        <>
          <Button
            kind="secondary"
            size="small"
            onClick={() => {
              setLoadingState(LoadingState.ACTIVE);

              importPrepaids(prepaids, app)
                .then(() => {
                  fetchPrepaids();
                  setLoadingState(LoadingState.FINISHED);
                })
                .catch(err => {
                  setErrorMessage(err.meta.error_detail);
                  setLoadingState(LoadingState.FINISHED);
                });
            }}
          >
            Import data from <br />
            documents/defaults
          </Button>
          <BaseTable
            disableBatch
            title="Items"
            headerData={tableHeaders}
            rawRowData={prepaids.items}
            rowData={prepaids.items.map(item => ({
              ...item,
              amount: item.amount === null ? "-" : formatCurrency(item.amount, 2, 2),
            }))}
            openModal={() => setModalIsOpen(true)}
            openDeleteModal={() => setDeleteModalIsOpen(true)}
            updateEditingMode={setEditingMode}
            initializeFormState={() => setFormState({})}
            updateFormState={data => setFormState(data)}
          />
        </>
      )}
    </HurdleView>
  );
};

export default Prepaids;
